import { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback, memo } from "react";
import { ChatMessage, Message, StatusStep } from "./ChatMessage";
import { ChatInput } from "./ChatInput";
import { PromptVariantSelector, PromptVariant } from "./PromptVariantSelector";
import { ScrollArea } from "@/components/ui/scroll-area";
import { WeightedPrompt, MusicConfig, LyriaState } from "@/lib/lyria-client";
import { PromptMode } from "@/hooks/use-lyria";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";

interface ChatPanelProps {
  lyriaState: LyriaState;
  currentPrompts: WeightedPrompt[];
  onSetPrompts: (prompts: WeightedPrompt[]) => void;
  onSetPromptsWithMode: (prompts: WeightedPrompt[], mode: PromptMode) => void;
  onUpdateConfig: (config: Partial<MusicConfig>, needsReset?: boolean) => void;
  onPlay: () => void;
}

export interface ChatPanelHandle {
  handleFilteredPrompt: (prompt: string) => void;
}

const INITIAL_MESSAGES: Message[] = [
  {
    id: "1",
    role: "assistant",
    content: "Hey! I'm your AI music collaborator. Describe the music you want to create—moods, genres, or artists for inspiration—and I'll generate it for you!",
    timestamp: new Date(),
  },
];

const MAX_FILTER_RETRIES = 2;

export const ChatPanel = memo(forwardRef<ChatPanelHandle, ChatPanelProps>(({
  lyriaState,
  currentPrompts,
  onSetPrompts,
  onSetPromptsWithMode,
  onUpdateConfig,
  onPlay,
}, ref) => {
  const [messages, setMessages] = useState<Message[]>(INITIAL_MESSAGES);
  const [isProcessing, setIsProcessing] = useState(false);
  const [promptVariant, setPromptVariant] = useState<PromptVariant>('focused');
  const scrollRef = useRef<HTMLDivElement>(null);
  const filterRetryCount = useRef(0);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Handle auto-rewriting filtered prompts whenever Lyria rejects the active prompt.
  const handleFilteredPrompt = useCallback(async (filteredPrompt: string) => {
    filterRetryCount.current += 1;
    console.log(`[ChatPanel] Handling filtered prompt (attempt ${filterRetryCount.current})`);
    Sentry.addBreadcrumb({
      category: "prompt.filter",
      level: "warning",
      message: "auto-heal attempt",
      data: { attempt: filterRetryCount.current, promptLength: filteredPrompt.length },
    });

    if (filterRetryCount.current > MAX_FILTER_RETRIES) {
      // Too many retries, ask user for help
      setMessages(prev => [
        ...prev,
        {
          id: Date.now().toString(),
          role: "assistant" as const,
          content: "The music generation system is having trouble with that prompt. Could you describe the style differently? Try using genre descriptions like 'funky house' or mood words like 'energetic' instead of specific names.",
          timestamp: new Date(),
        },
      ]);
      toast.error("Prompt was filtered. Try a broader style description.");
      filterRetryCount.current = 0;
      return;
    }

    // Add system message about auto-fix
    setMessages(prev => [
      ...prev,
      {
        id: Date.now().toString(),
        role: "system" as const,
        content: "Prompt was filtered. Rewriting it with safer musical descriptors...",
        timestamp: new Date(),
      },
    ]);

    try {
      // Call AI to rewrite the prompt (via Cloudflare Worker)
      const workerUrl = import.meta.env.VITE_WORKER_URL || 'http://localhost:8787';
      const response = await fetch(`${workerUrl}/chat-ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mode: 'rewrite_filtered',
          filteredPrompt,
        }),
      });

      if (!response.ok) {
        throw new Error(`Rewrite failed: ${response.status}`);
      }

      const data = await response.json();
      const rewrittenPrompt = data?.rewrittenPrompt;
      
      if (rewrittenPrompt) {
        console.log('[ChatPanel] Got rewritten prompt:', rewrittenPrompt);
        Sentry.addBreadcrumb({
          category: "prompt.filter",
          level: "info",
          message: "auto-heal success",
          data: { rewrittenLength: rewrittenPrompt.length },
        });
        
        // Update messages with the fix
        setMessages(prev => [
          ...prev,
          {
            id: Date.now().toString(),
            role: "system" as const,
            content: `Rewritten: "${rewrittenPrompt.substring(0, 60)}..."`,
            timestamp: new Date(),
          },
        ]);

        // Apply new prompt and retry
        onSetPrompts([{ text: rewrittenPrompt, weight: 1.0 }]);
        
        // Small delay before playing again
        setTimeout(() => {
          onPlay();
        }, 300);
      }
    } catch (error) {
      console.error('[ChatPanel] Failed to rewrite prompt:', error);
      Sentry.withScope((scope) => {
        scope.setTag("sf_component", "frontend");
        scope.setTag("sf_feature", "auto_heal");
        Sentry.captureException(error);
      });
      Sentry.addBreadcrumb({
        category: "prompt.filter",
        level: "error",
        message: "auto-heal failed",
      });
      setMessages(prev => [
        ...prev,
        {
          id: Date.now().toString(),
          role: "assistant" as const,
          content: "I couldn't automatically fix that prompt. Try describing the music using genres and moods instead of specific names.",
          timestamp: new Date(),
        },
      ]);
      filterRetryCount.current = 0;
    }
  }, [onSetPrompts, onPlay]);

  // Expose the handler to parent
  useImperativeHandle(ref, () => ({
    handleFilteredPrompt,
  }), [handleFilteredPrompt]);

  // Reset retry count and end initialization when successful playback starts
  useEffect(() => {
    if (lyriaState === 'playing') {
      filterRetryCount.current = 0;
    }
  }, [lyriaState]);

  const handleSend = async (content: string) => {
    Sentry.addBreadcrumb({
      category: "chat",
      level: "info",
      message: "user message sent",
      data: { length: content.length, variant: promptVariant },
    });
    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content,
      timestamp: new Date(),
    };

    const streamingMsgId = (Date.now() + 1).toString();

    setMessages((prev) => [
      ...prev,
      userMessage,
      {
        id: streamingMsgId,
        role: "assistant" as const,
        content: "",
        timestamp: new Date(),
        statusSteps: [],
      },
    ]);
    setIsProcessing(true);

    try {
      const workerUrl = import.meta.env.VITE_WORKER_URL || 'http://localhost:8787';
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(`${workerUrl}/chat-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userMessage: content,
          variant: promptVariant,
          conversationHistory: messages.slice(-10).map(m => ({
            role: m.role,
            content: m.content,
          })),
          currentContext: {
            isPlaying: lyriaState === 'playing' || lyriaState === 'starting' || lyriaState === 'paused',
            currentPrompts: currentPrompts,
          },
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`AI request failed: ${response.status}`);
      }
      if (!response.body) {
        throw new Error("AI response stream was empty");
      }

      // Helper to update the streaming assistant message in-place
      const updateStreamMsg = (updater: (msg: Message) => Partial<Message>) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === streamingMsgId ? { ...m, ...updater(m) } : m))
        );
      };

      // Parse SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      const processSseLine = (line: string) => {
        if (!line.startsWith("data: ")) return;
        let event: { type: string; step?: string; message?: string; data?: Record<string, unknown> };
        try {
          event = JSON.parse(line.slice(6));
        } catch {
          return;
        }

        if (event.type === "status") {
          updateStreamMsg((msg) => {
            const steps = (msg.statusSteps || []).map((s) => ({ ...s, done: true }));
            steps.push({ step: event.step!, message: event.message!, done: false });
            return { statusSteps: steps };
          });
        } else if (event.type === "result") {
          const aiResponse = event.data!;

          let messageContent = aiResponse.message as string;
          if (!messageContent || typeof messageContent !== "string") {
            messageContent = "Something went wrong - I didn't generate a response message.";
          }

          // Mark all steps as done and set content
          updateStreamMsg((msg) => ({
            statusSteps: (msg.statusSteps || []).map((s) => ({ ...s, done: true })),
            content: messageContent,
          }));

          // Apply prompts and config
          if (Array.isArray(aiResponse.prompts) && (aiResponse.prompts as unknown[]).length > 0) {
            const mode: PromptMode = (aiResponse.promptMode as PromptMode) || 'replace';
            console.log('[ChatPanel] Applying prompts with mode:', mode, aiResponse.prompts);
            onSetPromptsWithMode(aiResponse.prompts as WeightedPrompt[], mode);

            if (aiResponse.suggestedConfig) {
              const cfg = aiResponse.suggestedConfig as Partial<MusicConfig> & { scale?: string };
              const needsReset = cfg.bpm !== undefined || cfg.scale !== undefined;
              onUpdateConfig(cfg, needsReset);
            }

            filterRetryCount.current = 0;
            onPlay();

            setMessages((prev) => [
              ...prev,
              {
                id: (Date.now() + 2).toString(),
                role: "system" as const,
                content: "Generating music with Lyria RealTime...",
                timestamp: new Date(),
              },
            ]);
          }
        } else if (event.type === "error") {
          updateStreamMsg((msg) => ({
            statusSteps: (msg.statusSteps || []).map((s) => ({ ...s, done: true })),
            content: event.message || "An error occurred while processing your request.",
          }));
          toast.error(event.message || "Processing error");
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        // Keep last incomplete line in buffer
        buffer = lines.pop() || "";

        for (const line of lines) {
          processSseLine(line);
        }
      }
      buffer += decoder.decode();
      for (const line of buffer.split("\n")) {
        processSseLine(line.trim());
      }

    } catch (error) {
      console.error('[ChatPanel] Chat error:', error);

      let errorContent = "I had trouble processing that. Could you try describing the music differently?";
      let toastMessage = 'Failed to process message';

      if (error instanceof Error && error.name === 'AbortError') {
        errorContent = "That request took too long. Could you try again?";
        toastMessage = 'Request timed out';
      } else if (error instanceof TypeError && error.message.includes('fetch')) {
        if (error.message.includes('Failed to fetch')) {
          errorContent = "Cannot reach the server. Check your internet connection or try refreshing.";
          toastMessage = 'Server unreachable';
        } else if (error.message.includes('CORS')) {
          errorContent = "Configuration error. Please refresh the page or contact support.";
          toastMessage = 'CORS error - please refresh';
        } else {
          errorContent = "Network error. Check your connection and try again.";
          toastMessage = 'Network error - check your connection';
        }
      } else if (error instanceof Error) {
        const errorMsg = error.message.toLowerCase();
        if (errorMsg.includes('429') || errorMsg.includes('rate limit')) {
          errorContent = "Too many requests. Please wait a moment and try again.";
          toastMessage = 'Rate limit reached - please wait';
        } else if (errorMsg.includes('500') || errorMsg.includes('503')) {
          errorContent = "The AI service is experiencing issues. Try again in a moment.";
          toastMessage = 'Service temporarily unavailable';
        } else if (errorMsg.includes('401') || errorMsg.includes('403')) {
          errorContent = "Authentication error. Please refresh the page.";
          toastMessage = 'Authentication error';
        } else if (errorMsg.includes('json') || errorMsg.includes('parse')) {
          errorContent = "Received invalid response from AI. Please try again.";
          toastMessage = 'Invalid response format';
        } else if (errorMsg.includes('timeout')) {
          errorContent = "Request timed out. Please try again.";
          toastMessage = 'Request timed out';
        }
      }

      // Update the streaming message with error content
      setMessages((prev) =>
        prev.map((m) =>
          m.id === streamingMsgId
            ? { ...m, content: errorContent, statusSteps: (m.statusSteps || []).map((s) => ({ ...s, done: true })) }
            : m
        )
      );

      toast.error(toastMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-black">
      {/* Compact header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
        <div
          className={`w-2 h-2 rounded-full ${
            lyriaState === 'playing'
              ? 'bg-green-500 animate-pulse'
              : lyriaState === 'starting' || lyriaState === 'buffering'
                ? 'bg-yellow-500 animate-pulse'
                : lyriaState === 'connected'
                  ? 'bg-green-500'
                  : lyriaState === 'connecting'
                    ? 'bg-yellow-500 animate-pulse'
                    : 'bg-muted-foreground'
          }`}
        />
        <span className="text-sm font-medium text-foreground">AI Chat</span>
        <span className="text-xs text-muted-foreground ml-auto">
          {lyriaState === 'playing' ? 'Synthesizing' :
           lyriaState === 'starting' ? 'Starting' :
           lyriaState === 'connected' ? 'Ready' :
           lyriaState === 'connecting' ? 'Connecting' :
           'Idle'}
        </span>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-4" ref={scrollRef}>
        <div className="py-6 space-y-4">
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}
        </div>
      </ScrollArea>

      {/* Prompt Variant Selector */}
      <PromptVariantSelector value={promptVariant} onChange={setPromptVariant} />

      {/* Input */}
      <div className="p-4 border-t border-white/5">
        <ChatInput
          onSend={handleSend}
          disabled={isProcessing}
          placeholder="Describe the music you want to create..."
        />
      </div>
    </div>
  );
}));

ChatPanel.displayName = 'ChatPanel';
